/**
 * AssetCache Utility
 * Uses the Browser Cache API to store audio and image files locally.
 * This ensures media files are available offline and load instantly.
 */

const CACHE_NAME = 'flashcard-assets-v1';

export const AssetCache = {
  /**
   * Pre-load a list of URLs into the cache.
   */
  async preload(urls: (string | undefined)[]) {
    if (typeof window === 'undefined' || !('caches' in window)) return;
    
    const validUrls = urls.filter((url): url is string => !!url && (url.startsWith('http') || url.startsWith('/')));
    if (validUrls.length === 0) return;

    try {
      const cache = await caches.open(CACHE_NAME);
      // We use addAll but wrap in individual try-catch to avoid one failure blocking all
      await Promise.all(
        validUrls.map(async (url) => {
          try {
            const response = await fetch(url, { mode: 'no-cors' });
            if (response) {
              await cache.put(url, response);
            }
          } catch (err) {
            console.warn(`[Cache] Failed to preload: ${url}`, err);
          }
        })
      );
      console.log(`[Cache] Preloaded ${validUrls.length} assets.`);
    } catch (err) {
      console.error('[Cache] Storage error:', err);
    }
  },

  /**
   * Check if a URL is already cached and return its blob URL or original URL.
   */
  async get(url: string | undefined): Promise<string | undefined> {
    if (!url || typeof window === 'undefined' || !('caches' in window)) return url;

    try {
      const cache = await caches.open(CACHE_NAME);
      const cachedResponse = await cache.match(url);
      
      if (cachedResponse) {
        const blob = await cachedResponse.blob();
        return URL.createObjectURL(blob);
      }
    } catch (err) {
      console.warn(`[Cache] Fetch from cache failed for ${url}`, err);
    }
    
    return url;
  },

  /**
   * Clear the entire asset cache.
   */
  async clear() {
    if (typeof window === 'undefined' || !('caches' in window)) return;
    await caches.delete(CACHE_NAME);
  }
};
